import React, { useEffect, useCallback, useState, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { measuringMachine } from "./measuringMachine";
import { useMachine } from "@xstate/react";

import "@tensorflow/tfjs-backend-webgl";
import * as tfjsWasm from "@tensorflow/tfjs-backend-wasm";
import * as tf from "@tensorflow/tfjs-core";

import FaceScanner from "./FaceScanner.js";

import "./FaceScanner.css";

import LoadingModelPage from "./LoadingModelPage";

import { Typography, Box, Button, LinearProgress } from "@mui/material";

import {
  SCAN_INTERVAL_IN_MILLISECONDS,
  SCAN_TIMEOUT_IN_MILLISECONDS,
  THROTTLE_INTERVAL_IN_MILLISECONDS
} from "./MugShotConfig";

import sendingImage from "./img/sending.gif";
import checkmarkImg from "./img/checkmark_circle.png";

import MaskSelectorToolStyles from "../../../pages/MaskSelectorTool/MaskSelectorToolStyles";

import { resMedSuccess } from "../../patientTheme";
import { useTranslation } from "react-i18next";

import { getLandmarks, analyseImages } from "resmed-mugshot-image-analyzer";

import { loadModelPipeline } from "../MugshotModelLoader";

tfjsWasm.setWasmPaths(
  `/wasm/tensorflow/tfjs-backend-wasm/${tfjsWasm.version_wasm}/dist/`
);

const useStyles = makeStyles()((theme) => ({
  root: {
    textAlign: "center",
    position: "relative",
    width: "100%",
  },

  buttonBox: {
    marginTop: 132,
  },

  button: {
    width: 264,
    height: 50,
    fontSize: "20px",
    color: resMedSuccess,
    background: "white",
  },
}));

// NOTE: This is called the analysing page here because that's what we are doing but our messaging to the user
//       indicates that we are sending their data so this page may also be referred to as the "sending" page.
const AnalysingPage = ({ progress }) => {
  const {classes} = MaskSelectorToolStyles();

  const { t } = useTranslation("pages.MaskSelectorTool.FaceMeasurer");
  const sendingMessage = t("sendingMessage");
  const sendingStatus = t("sendingStatus");
  const sendingProgress = t("sendingProgress", { progress: progress });

  const messageRef = useRef(null);

  useEffect(() => {
    if(messageRef.current) {
      messageRef.current.focus();
    }
  });

  return (
    <Box className={classes.mainBox}>
      <Box className={classes.centeredBox}>
        <Box className={classes.paddingBox}>
          <Typography color="textPrimary" align="left" variant="subtitle1" tabIndex="0" className={classes.noOutline}
                      aria-label={sendingMessage} ref={messageRef}>
            {sendingMessage}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.centeredBox}>
        <Box className={classes.doublePaddingBox}>
          <img alt="" src={sendingImage} />
        </Box>
      </Box>
      <Box className={classes.centeredText}>
        <Box className={classes.paddingBox}>
          <Typography color="textPrimary" variant="body2" tabIndex="0"
                      aria-label={sendingStatus} className={classes.noOutline} >
            {sendingStatus}
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        className={classes.progressBar}
      />
      <Box className={classes.centeredText}>
        <Box className={classes.paddingBox}>
          <Typography color="textPrimary" variant="body1" tabIndex="0"
                      aria-label={sendingProgress} className={classes.noOutline}>
            {sendingProgress}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ScanCompletePage = ({ handleNext }) => {
  const { t } = useTranslation("pages.MaskSelectorTool.FaceMeasurer");

  // Localization strings
  const scanCompleteAltText = t("scanCompleteAltText");
  const nextButton = t("nextButton");

  const {classes} = useStyles();

  const checkRef = useRef(null);

  useEffect(() => {
    if(checkRef.current) {
      checkRef.current.focus();
    }
  });

  const {classes: commonClasses} = MaskSelectorToolStyles();

  return (
    <Box sx={{textAlign: "center"}}>
      <Box>
        <Box pt="175px">
          <img src={checkmarkImg} className={commonClasses.noOutline} alt={scanCompleteAltText} tabIndex="0"
               aria-label={scanCompleteAltText} ref={checkRef} />
        </Box>
      </Box>

      <Box className={classes.buttonBox} mt="20px">
        <Button
          disableRipple
          variant="contained"
          className={classes.button}
          onClick={handleNext}
        >
          {nextButton}
        </Button>
      </Box>
    </Box>
  );
};

const FaceMeasurer = ({
  onComplete,
  onFailed,
  stateListener,
  scannerStateListener,
  onUserMediaError,
  debug = false,
  timeoutLength = SCAN_TIMEOUT_IN_MILLISECONDS,
  interval = SCAN_INTERVAL_IN_MILLISECONDS,
  model = undefined,
  modelLoadingPromise = undefined,
  setBackButtonOverride = undefined,
  setHelpButtonOverride = undefined,
  imageCaptureListener = undefined,
  cameraId = undefined,
  setCameraId = undefined,
  showNoCameraAccessDialog = false,
  cameraInfoArray,
  setCameraInfoArray,
}) => {
  const {classes} = useStyles();

  const [progress, setProgress] = useState(0);
  const [locallyLoadedModel, setLocallyLoadedModel] = useState(null);

  const waitForModel = async () => {
    // If we are given a promise then wait for the promise.  No need to wait for
    // a promise if we are given the model with no promise.
    if ( modelLoadingPromise !== undefined ) {
      await modelLoadingPromise.then(Promise.resolve());
    }
  };


  // We provide a loader in the measurer, so we don't have to preload the model.  This makes
  // creating tools pages in the test app easier to make.
  const localLoadModel = async () => {

    if (tf.getBackend() !== "wasm") {
      await tf.setBackend("wasm");
      await tf.ready();
    }

    if (locallyLoadedModel !== null) {
      // Model is already loaded.  Nothing to load.
    } else {
      let values = await Promise.all([loadModelPipeline(true)]);
      setLocallyLoadedModel(values[0]);
    }
  };

  const setProgressCallback = ( p ) => {
    let value = Math.round(p);

    if ( value === 100 ) {
      // Never show a value of 100% on the progress bar as the user
      // may think processing is done when we are in the process of
      // switching pages/sending data to the server, and we don't want
      // them to quit the application early.
      value = 99;
    }

    setProgress(value)
  }

  const analyseImagesCallback  = ( ) => async (context) => {

    let analysisModel = model === undefined ? locallyLoadedModel : model;

    let analyzeFunction = analyseImages( getLandmarks(true, true),
        p => setProgressCallback(p),
        analysisModel,
        debug );

    return analyzeFunction(context);
  }

  const [state, send, service] = useMachine(measuringMachine, {
    services: {
      loadModel: model === undefined && modelLoadingPromise === undefined ? localLoadModel : waitForModel,
      analyseImages: analyseImagesCallback(),
    },
  });

  useEffect(() => {
    if (debug) {
      console.log(`transitioning to state ${JSON.stringify(state.value)}`);
    }

    if (state.value === "checkForErrors") {
      send("success");
    }

    if (state.value === "scanningCompletePause") {
      if (setBackButtonOverride !== undefined) {
        setBackButtonOverride(true);
      }
      if (setHelpButtonOverride !== undefined) {
        setHelpButtonOverride(true);
      }
    } else {
      if (setBackButtonOverride !== undefined) {
        setBackButtonOverride(false);
      }
      if (setHelpButtonOverride !== undefined) {
        setHelpButtonOverride(false);
      }
    }

    if (stateListener !== undefined) {
      stateListener(state.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, debug, send, setBackButtonOverride, setHelpButtonOverride]); // Don't add stateListener here. It will break face scanning.

  useEffect(() => {
    const listener = (e) => {
      if (e.data.status === "complete") {
        onComplete({
          measurements: e.data.measurements,
          images: e.data.images,
          throttle_interval: THROTTLE_INTERVAL_IN_MILLISECONDS
        });
      } else {
        onFailed();
      }
    };

    service.onDone(listener);

    return () => {
      service.off(listener);
    };
  }, [service]);

  const handleFaceScannerComplete = useCallback(
    (images) => {
      send("SCANNINGCOMPLETE", { images });
    },
    [send]
  );

  const completeToAnalysing = () => {
    send("NEXT");
  };

  const showSendingPage = (state) => {
    return (
      state.matches("analysing") ||
      state.matches("checkForErrors") ||
      state.matches("complete")
    );
  };

  return (
    <div className={classes.root}>
      {state.matches("loading") && <LoadingModelPage />}

      {state.matches("scanning") && (
        <FaceScanner
          model={model === undefined && modelLoadingPromise === undefined ? locallyLoadedModel : model}
          landmarks={getLandmarks(false)}
          interval={interval}
          onComplete={handleFaceScannerComplete}
          timeoutLength={timeoutLength}
          scannerStateListener={scannerStateListener}
          imageCaptureListener={imageCaptureListener}
          cameraId={cameraId}
          setCameraId={setCameraId}
          onUserMediaError={onUserMediaError}
          showNoCameraAccessDialog={showNoCameraAccessDialog}
          cameraInfoArray={cameraInfoArray}
          setCameraInfoArray={setCameraInfoArray}
        />
      )}

      {showSendingPage(state) && <AnalysingPage progress={progress} />}

      {state.matches("scanningCompletePause") && (
        <ScanCompletePage handleNext={completeToAnalysing} />
      )}
    </div>
  );
};

export default FaceMeasurer;

export { AnalysingPage, getLandmarks };
